import { PageTitle } from "@/components/base/PageTitle";
import { Friendly, KNOWN_ERRORS } from "@/lib/friendly";
import {
    Alert,
    AlertDescription,
    AlertTitle,
    PageContainerNewLayout,
} from "@disasterscience/scaffoldy";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FriendlyError } from "./FriendlyError";

export type ErrorViewState = {
    knownError?: KNOWN_ERRORS;
    errorMessage?: string;
    errorDetail?: string;
};

export const ErrorView = (props: ErrorViewState) => {
    const location = useLocation();
    const errorState: ErrorViewState | null = location.state;
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
    const [knownError, setKnownError] = useState<KNOWN_ERRORS | undefined>(props.knownError);
    // The API surfaces a per-request reference id on rejection paths so a
    // user-reported "I couldn't log in" can be traced through OpenSearch.
    const [correlationId, setCorrelationId] = useState<string | undefined>(undefined);

    useEffect(() => {
        // Handle when state is passed from another component
        if (errorState) {
            setErrorMessage(errorState.errorMessage);
            setErrorDetail(errorState.errorDetail);
            console.error("ErrorView State", errorState);
        }

        // Handle when a code is passed back from the API
        const qs = new URLSearchParams(location.search);
        const errorCode = qs.get("code");
        if (errorCode) {
            const knownError = Friendly.KnownError(errorCode);

            if (knownError) {
                setKnownError(knownError);
            } else {
                setErrorMessage("API returned error code");
                setErrorDetail(errorCode);
            }
        }

        const correlationIdParam = qs.get("correlation_id");
        if (correlationIdParam) {
            setCorrelationId(correlationIdParam);
        }
    }, [errorState, location.search]);

    const render = () => {
        if (knownError) {
            return (
                <>
                    <PageTitle title="Error" />
                    <PageContainerNewLayout>
                        <>
                            {
                                {
                                    FB_LOGIN_ERROR_UNABLE_TO_VALIDATE_TOKEN_OR_EMAIL_NOT_VALIDATED_WITH_FB:
                                        (
                                            <>
                                                <Alert variant="warning">
                                                    <AlertTitle>
                                                        Unable to login with Facebook
                                                    </AlertTitle>
                                                    <AlertDescription>
                                                        We asked Facebook to verify your details,
                                                        they were unable to confirm your account.
                                                        Please use another sign in method, or verify
                                                        your email with Facebook by following this
                                                        guide{" "}
                                                        <a
                                                            href="https://dstr.to/JwP6Zl"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            How to verify your Facebook account
                                                        </a>
                                                        .
                                                    </AlertDescription>
                                                </Alert>
                                            </>
                                        ),
                                    AP_LOGIN_ERROR_NO_EMAIL_OR_NAME: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Unable to login with Apple</AlertTitle>
                                                <AlertDescription>
                                                    When we asked Apple to verify your details, they
                                                    were not able to confirm your account. Please
                                                    sign in with another method, or review this
                                                    guide{" "}
                                                    <a
                                                        href="https://dstr.to/JwP6Zl"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        How to verify your Apple account
                                                    </a>
                                                    .
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    FB_LOGIN_ERROR_UNABLE_TO_UNPACK: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Unable to login with Apple</AlertTitle>
                                                <AlertDescription>
                                                    After asking Apple to verify your details, we
                                                    received a response which we were not able to
                                                    understand.Please sign in with another method,
                                                    or review this guide{" "}
                                                    <a
                                                        href="https://dstr.to/JwP6Zl"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        How to verify your Apple account
                                                    </a>
                                                    .
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    LOGIN_ERROR_UPDATE_REQUIRED: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Please update your app</AlertTitle>
                                                <AlertDescription>
                                                    For your account security, the sign-in flow has
                                                    been updated. Please update to the latest
                                                    version of the Bushfire.io app to continue. If
                                                    you're on the web, refresh the page to load the
                                                    new version.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    // OTC_* codes are surfaced inline by
                                    // LoginView's LoginCode dialog rather than
                                    // here, but the typed lookup needs an arm
                                    // for every KNOWN_ERRORS variant.
                                    OTC_INVALID_OR_EXPIRED: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>
                                                    Sign-in code didn't validate
                                                </AlertTitle>
                                                <AlertDescription>
                                                    That code didn't match, or it has expired.
                                                    Please restart the sign-in to request a new
                                                    code.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    OTC_USER_ALREADY_EXISTS: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Account already exists</AlertTitle>
                                                <AlertDescription>
                                                    An account with this email already exists.
                                                    Please sign in with that account.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    OTC_ROOT_USER_NOT_PERMITTED: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Sign-in not available</AlertTitle>
                                                <AlertDescription>
                                                    This account can't sign in with a code. Please
                                                    use a different sign-in method.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                    LOGIN_ERROR_INVALID_STATE: (
                                        <>
                                            <Alert variant="warning">
                                                <AlertTitle>Unable to login</AlertTitle>
                                                <AlertDescription>
                                                    We couldn't complete the login process. This can
                                                    be caused by having multiple tabs open and
                                                    trying to sign in from more than one of them,
                                                    but could also be caused by overly aggressive
                                                    adblockers or similar extensions. Please try
                                                    signing in again, try disabling any extensions
                                                    that might interfere with the login process.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    ),
                                }[knownError]
                            }
                            {correlationId && (
                                <p className="tw:mt-2 tw:text-xs tw:text-center tw:font-mono tw:text-gray-500">
                                    Reference: {correlationId}
                                </p>
                            )}
                        </>
                    </PageContainerNewLayout>
                </>
            );
        }
        if (errorMessage) {
            return (
                <>
                    <PageTitle title="Error" />
                    <PageContainerNewLayout>
                        <FriendlyError errorName={errorMessage} errorStack={errorDetail} />
                        {correlationId && (
                            <p className="tw:mt-2 tw:text-xs tw:text-center tw:font-mono tw:text-gray-500">
                                Reference: {correlationId}
                            </p>
                        )}
                    </PageContainerNewLayout>
                </>
            );
        } else {
            return (
                <>
                    <PageTitle title="Error" />
                    <PageContainerNewLayout>
                        <FriendlyError errorName="I navigated to the error page" />
                    </PageContainerNewLayout>
                </>
            );
        }
    };

    return render();
};
